import {
    type CSSProperties,
    type ReactNode,
    useEffect,
    useState,
    useRef
} from "react";
import {
    View
} from "react-native";
import {
    ComponentHeader,
    AnatomySection,
    ExampleSection,
    getSectionToc,
    PropsTable
} from "./shared";
import {
    type ISwipeableRowRef,
    type SwipeableRowSide,
    type ISwipeRowAction,
    type NCoreUIKitIcon,
    NCoreUIKitTheme,
    SwipeableRow,
    Button,
    Item,
    Text
} from "ncore-ui-kit";
import {
    useDocsI18n
} from "../../i18n";
import type {
    DocsTranslate
} from "../../anatomy/types";
import {
    Trash2
} from "lucide-react-native";
import {
    type DocsStyleTokens,
    BulletList,
    InlineCode,
    Paragraph,
    CodeBlock,
    DemoFrame,
    Article,
    Preview,
    Section
} from "../../components/content";
import {
    CheckIcon,
    BellIcon,
    FileIcon,
    InfoIcon,
    UserIcon
} from "../../anatomy/icons";

const NAME = "SwipeableRow";

const USAGE_CODE = `<SwipeableRow
    description="Commented on your report"
    title="Ada Lovelace"
    leadingActions={[
        {
            onPress: () => markRead(message),
            title: "Mark as read",
            icon: CheckIcon,
            key: "read"
        }
    ]}
    trailingActions={[
        {
            onPress: () => removeMessage(message),
            undoMessage: "Message deleted",
            title: "Delete",
            icon: TrashIcon,
            isUndoable: true,
            type: "danger",
            key: "delete"
        },
        {
            onPress: () => archive(message),
            title: "Archive",
            icon: FileIcon,
            key: "archive"
        }
    ]}
>
    <Item
        onPress={() => openMessage(message)}
    >
        <Item.Media
            icon={UserIcon}
        />
        <Item.Content>
            <Item.Title>
                Ada Lovelace
            </Item.Title>
            <Item.Description>
                Commented on your report
            </Item.Description>
        </Item.Content>
        <Item.Actions>
            <Text>14:32</Text>
        </Item.Actions>
    </Item>
</SwipeableRow>`;

const REF_CODE = `const rowRef = useRef<ISwipeableRowRef>(null);

const [openSide, setOpenSide] = useState<SwipeableRowSide | null>(null);

<SwipeableRow
    onSwipeClose={() => setOpenSide(null)}
    interactionMode="swipe"
    onSwipeOpen={setOpenSide}
    ref={rowRef}
/>

rowRef.current?.open("trailing");

rowRef.current?.close();`;

type NotificationType = {
    messageKey: string;
    isRead: boolean;
    name: string;
    time: string;
    key: string;
};

const NOTIFICATIONS: Array<NotificationType> = [
    {
        messageKey: "swipeableRowPage.messageComment",
        name: "Ada Lovelace",
        isRead: false,
        time: "14:32",
        key: "ada"
    },
    {
        messageKey: "swipeableRowPage.messageAssign",
        name: "Alan Turing",
        isRead: false,
        time: "11:48",
        key: "alan"
    },
    {
        messageKey: "swipeableRowPage.messageApprove",
        name: "Grace Hopper",
        time: "09:05",
        isRead: true,
        key: "grace"
    }
];

const SWIPE_LABEL = "interactionMode=\"swipe\"";

const POINTER_LABEL = "interactionMode=\"pointer\"";

const getDemoStyle = ({
    spaces
}: DocsStyleTokens): CSSProperties => ({
    flexDirection: "column",
    gap: spaces.spacingSm,
    alignItems: "stretch",
    display: "flex",
    width: "100%"
});

const getButtonsStyle = ({
    spaces
}: DocsStyleTokens): CSSProperties => ({
    gap: spaces.spacingSm,
    flexWrap: "wrap",
    display: "flex"
});

const FRAME_STYLE = {
    width: "100%"
};

const ListFrame = ({
    children
}: {
    children: ReactNode;
}) => {
    const {
        shapeConfigs,
        spaces
    } = NCoreUIKitTheme.useContext();

    return <div
        style={{
            ...FRAME_STYLE,
            maxWidth: shapeConfigs.dialogMaxWidth
        }}
    >
        <DemoFrame>
            <View
                style={{
                    padding: spaces.spacingXs
                }}
            >
                {children}
            </View>
        </DemoFrame>
    </div>;
};

const TrashIcon: NCoreUIKitIcon = ({
    customColor,
    size
}) => <Trash2
    color={customColor}
    size={size}
/>;

const handleRowPress = () => undefined;

const renderRow = (title: string, description: string, time: string) => <Item
    onPress={handleRowPress}
>
    <Item.Media
        icon={UserIcon}
    />
    <Item.Content>
        <Item.Title>
            {title}
        </Item.Title>
        <Item.Description>
            {description}
        </Item.Description>
    </Item.Content>
    <Item.Actions>
        <Text
            variant="bodySmallSize"
            color="mid"
        >
            {time}
        </Text>
    </Item.Actions>
</Item>;

const NotificationList = () => {
    const docsTokens = NCoreUIKitTheme.useContext();

    const {
        t
    } = useDocsI18n();

    const [
        notifications,
        setNotifications
    ] = useState(NOTIFICATIONS);

    const handleToggleRead = (key: string) => {
        setNotifications((current) => current.map((item) => item.key === key ? {
            ...item,
            isRead: !item.isRead
        } : item));
    };

    const handleRemove = (key: string) => {
        setNotifications((current) => current.filter((item) => item.key !== key));
    };

    const handleReset = () => setNotifications(NOTIFICATIONS);

    const getLeadingActions = (item: NotificationType): Array<ISwipeRowAction> => [
        {
            title: item.isRead ? t("swipeableRowPage.markUnread") : t("swipeableRowPage.markRead"),
            onPress: () => handleToggleRead(item.key),
            icon: CheckIcon,
            key: "read"
        }
    ];

    const getTrailingActions = (item: NotificationType): Array<ISwipeRowAction> => [
        {
            undoMessage: t("swipeableRowPage.deleted"),
            onPress: () => handleRemove(item.key),
            title: t("swipeableRowPage.delete"),
            isUndoable: true,
            icon: TrashIcon,
            type: "danger",
            key: "delete"
        },
        {
            onPress: () => handleRemove(item.key),
            title: t("swipeableRowPage.archive"),
            icon: FileIcon,
            key: "archive",
            shortcut: "E"
        }
    ];

    return <div
        style={getDemoStyle(docsTokens)}
    >
        <ListFrame>
            {
                notifications.length ?
                    notifications.map((item) => <SwipeableRow
                        trailingActions={getTrailingActions(item)}
                        leadingActions={getLeadingActions(item)}
                        description={t(item.messageKey)}
                        title={item.name}
                        key={item.key}
                    >
                        {renderRow(item.name, t(item.messageKey), item.time)}
                    </SwipeableRow>)
                :
                    <Text
                        variant="bodyMediumSize"
                        color="mid"
                    >
                        {t("swipeableRowPage.empty")}
                    </Text>
            }
        </ListFrame>
        <div
            style={getButtonsStyle(docsTokens)}
        >
            <Button
                title={t("swipeableRowPage.reset")}
                onPress={handleReset}
                variant="outline"
                type="neutral"
            />
        </div>
    </div>;
};

const ModesDemo = () => {
    const docsTokens = NCoreUIKitTheme.useContext();

    const {
        t
    } = useDocsI18n();

    const rowRef = useRef<ISwipeableRowRef>(null);

    const [
        event,
        setEvent
    ] = useState("");

    const leadingActions: Array<ISwipeRowAction> = [
        {
            title: t("swipeableRowPage.markRead"),
            icon: CheckIcon,
            key: "read"
        }
    ];

    const trailingActions: Array<ISwipeRowAction> = [
        {
            title: t("swipeableRowPage.archive"),
            icon: FileIcon,
            key: "archive",
            shortcut: "E"
        },
        {
            title: t("swipeableRowPage.delete"),
            icon: TrashIcon,
            type: "danger",
            key: "delete"
        }
    ];

    const handleOpenLeading = () => rowRef.current?.open("leading");

    const handleOpenTrailing = () => rowRef.current?.open("trailing");

    const handleClose = () => rowRef.current?.close();

    const handleSwipeOpen = (side: SwipeableRowSide) => setEvent(`onSwipeOpen("${side}")`);

    const handleSwipeClose = () => setEvent("onSwipeClose()");

    return <>
        <div
            style={getDemoStyle(docsTokens)}
        >
            <InlineCode>
                {SWIPE_LABEL}
            </InlineCode>
            <ListFrame>
                <SwipeableRow
                    description={t("swipeableRowPage.messageComment")}
                    trailingActions={trailingActions}
                    leadingActions={leadingActions}
                    onSwipeClose={handleSwipeClose}
                    onSwipeOpen={handleSwipeOpen}
                    interactionMode="swipe"
                    title="Ada Lovelace"
                    ref={rowRef}
                >
                    {renderRow("Ada Lovelace", t("swipeableRowPage.messageComment"), "14:32")}
                </SwipeableRow>
            </ListFrame>
            <div
                style={getButtonsStyle(docsTokens)}
            >
                <Button
                    title={t("swipeableRowPage.openLeading")}
                    onPress={handleOpenLeading}
                    variant="outline"
                    type="neutral"
                />
                <Button
                    title={t("swipeableRowPage.openTrailing")}
                    onPress={handleOpenTrailing}
                    variant="outline"
                    type="neutral"
                />
                <Button
                    title={t("swipeableRowPage.close")}
                    onPress={handleClose}
                    variant="outline"
                    type="neutral"
                />
            </div>
            <Text
                variant="bodySmallSize"
                color="mid"
            >
                {event ? t("swipeableRowPage.lastEvent", [
                    event
                ]) : t("swipeableRowPage.noEvent")}
            </Text>
        </div>
        <div
            style={getDemoStyle(docsTokens)}
        >
            <InlineCode>
                {POINTER_LABEL}
            </InlineCode>
            <ListFrame>
                <SwipeableRow
                    description={t("swipeableRowPage.messageAssign")}
                    trailingActions={trailingActions}
                    leadingActions={leadingActions}
                    interactionMode="pointer"
                    title="Alan Turing"
                >
                    {renderRow("Alan Turing", t("swipeableRowPage.messageAssign"), "11:48")}
                </SwipeableRow>
            </ListFrame>
        </div>
    </>;
};

const ActionsDemo = () => {
    const docsTokens = NCoreUIKitTheme.useContext();

    const {
        t
    } = useDocsI18n();

    const rowRef = useRef<ISwipeableRowRef>(null);

    useEffect(() => {
        rowRef.current?.open("trailing");
    }, []);

    const leadingActions: Array<ISwipeRowAction> = [
        {
            title: t("swipeableRowPage.remind"),
            icon: BellIcon,
            key: "remind",
            type: "info"
        },
        {
            title: t("swipeableRowPage.complete"),
            icon: CheckIcon,
            type: "success",
            key: "complete"
        }
    ];

    const trailingActions: Array<ISwipeRowAction> = [
        {
            title: t("swipeableRowPage.delete"),
            icon: TrashIcon,
            type: "danger",
            key: "delete"
        },
        {
            title: t("swipeableRowPage.report"),
            type: "warning",
            icon: InfoIcon,
            key: "report"
        },
        {
            title: t("swipeableRowPage.archive"),
            type: "neutral",
            icon: FileIcon,
            key: "archive"
        }
    ];

    const handleOpenLeading = () => rowRef.current?.open("leading");

    const handleOpenTrailing = () => rowRef.current?.open("trailing");

    const handleClose = () => rowRef.current?.close();

    return <div
        style={getDemoStyle(docsTokens)}
    >
        <ListFrame>
            <SwipeableRow
                description={t("swipeableRowPage.messageApprove")}
                trailingActions={trailingActions}
                leadingActions={leadingActions}
                interactionMode="swipe"
                title="Grace Hopper"
                ref={rowRef}
            >
                {renderRow("Grace Hopper", t("swipeableRowPage.messageApprove"), "09:05")}
            </SwipeableRow>
        </ListFrame>
        <div
            style={getButtonsStyle(docsTokens)}
        >
            <Button
                title={t("swipeableRowPage.openLeading")}
                onPress={handleOpenLeading}
                variant="outline"
                type="neutral"
            />
            <Button
                title={t("swipeableRowPage.openTrailing")}
                onPress={handleOpenTrailing}
                variant="outline"
                type="neutral"
            />
            <Button
                title={t("swipeableRowPage.close")}
                onPress={handleClose}
                variant="outline"
                type="neutral"
            />
        </div>
    </div>;
};

const FullSwipeDemo = () => {
    const docsTokens = NCoreUIKitTheme.useContext();

    const {
        t
    } = useDocsI18n();

    const [
        reachCount,
        setReachCount
    ] = useState(0);

    const [
        runCount,
        setRunCount
    ] = useState(0);

    const handleFullSwipeReach = () => setReachCount((count) => count + 1);

    const handleRun = () => setRunCount((count) => count + 1);

    const leadingActions: Array<ISwipeRowAction> = [
        {
            title: t("swipeableRowPage.complete"),
            onPress: handleRun,
            icon: CheckIcon,
            type: "success",
            key: "complete"
        }
    ];

    const trailingActions: Array<ISwipeRowAction> = [
        {
            title: t("swipeableRowPage.archive"),
            onPress: handleRun,
            icon: FileIcon,
            key: "archive"
        }
    ];

    return <div
        style={getDemoStyle(docsTokens)}
    >
        <ListFrame>
            <SwipeableRow
                description={t("swipeableRowPage.messageAssign")}
                onFullSwipeReach={handleFullSwipeReach}
                trailingActions={trailingActions}
                leadingActions={leadingActions}
                isFullSwipeEnabled={true}
                interactionMode="swipe"
                title="Alan Turing"
            >
                {renderRow("Alan Turing", t("swipeableRowPage.messageAssign"), "11:48")}
            </SwipeableRow>
        </ListFrame>
        <Text
            variant="bodySmallSize"
            color="mid"
        >
            {t("swipeableRowPage.fullReadout", [
                reachCount,
                runCount
            ])}
        </Text>
    </div>;
};

export const getToc = (t: DocsTranslate) => getSectionToc([
    {
        title: t("section.overview"),
        id: "overview"
    },
    {
        title: t("swipeableRowPage.modesTitle"),
        id: "modes"
    },
    {
        title: t("swipeableRowPage.actionsTitle"),
        id: "actions"
    },
    {
        title: t("swipeableRowPage.fullTitle"),
        id: "full"
    },
    {
        title: t("section.accessibility"),
        id: "accessibility"
    },
    {
        title: t("section.props"),
        id: "props"
    }
], NAME, t);

const SwipeableRowPage = () => {
    const {
        t
    } = useDocsI18n();

    return <Article>
        <ComponentHeader
            lead={t("swipeableRowPage.lead")}
            name={NAME}
        />
        <Section
            title={t("section.overview")}
            id="overview"
        >
            <Paragraph>
                {t("swipeableRowPage.overviewText")}
            </Paragraph>
            <Preview
                caption={t("swipeableRowPage.overviewCaption")}
                isStacked={true}
            >
                <NotificationList/>
            </Preview>
            <CodeBlock
                code={USAGE_CODE}
                language="tsx"
            />
        </Section>
        <AnatomySection
            name={NAME}
        />
        <Section
            title={t("swipeableRowPage.modesTitle")}
            id="modes"
        >
            <Paragraph>
                {t("swipeableRowPage.modesText")}
            </Paragraph>
            <Preview
                caption={t("swipeableRowPage.modesCaption")}
                isStacked={true}
            >
                <ModesDemo/>
            </Preview>
            <CodeBlock
                code={REF_CODE}
                language="tsx"
            />
        </Section>
        <Section
            title={t("swipeableRowPage.actionsTitle")}
            id="actions"
        >
            <Paragraph>
                {t("swipeableRowPage.actionsText")}
            </Paragraph>
            <Preview
                caption={t("swipeableRowPage.actionsCaption")}
                isStacked={true}
            >
                <ActionsDemo/>
            </Preview>
        </Section>
        <Section
            title={t("swipeableRowPage.fullTitle")}
            id="full"
        >
            <Paragraph>
                {t("swipeableRowPage.fullText")}
            </Paragraph>
            <Preview
                caption={t("swipeableRowPage.fullCaption")}
                isStacked={true}
            >
                <FullSwipeDemo/>
            </Preview>
        </Section>
        <Section
            title={t("section.accessibility")}
            id="accessibility"
        >
            <BulletList
                items={[
                    t("swipeableRowPage.a11ySwipe"),
                    t("swipeableRowPage.a11yHidden"),
                    t("swipeableRowPage.a11ySheet"),
                    t("swipeableRowPage.a11yPointer"),
                    t("swipeableRowPage.a11yKeyboard"),
                    t("swipeableRowPage.a11yUndo"),
                    t("swipeableRowPage.a11yMotion")
                ]}
            />
        </Section>
        <Section
            title={t("section.props")}
            id="props"
        >
            <PropsTable
                rows={[
                    {
                        description: t("swipeableRowPage.propChildren"),
                        type: "ReactElement<IItemProps>",
                        name: "children"
                    },
                    {
                        description: t("swipeableRowPage.propTitle"),
                        name: "title",
                        type: "string"
                    },
                    {
                        description: t("swipeableRowPage.propDescription"),
                        name: "description",
                        type: "string"
                    },
                    {
                        description: t("swipeableRowPage.propIsActionLabelVisible"),
                        defaultValue: "true when description is set",
                        name: "isActionLabelVisible",
                        type: "boolean"
                    },
                    {
                        description: t("swipeableRowPage.propLeadingActions"),
                        type: "Array<ISwipeRowAction>",
                        name: "leadingActions"
                    },
                    {
                        description: t("swipeableRowPage.propTrailingActions"),
                        type: "Array<ISwipeRowAction>",
                        name: "trailingActions"
                    },
                    {
                        description: t("swipeableRowPage.propInteractionMode"),
                        type: "\"auto\" | \"swipe\" | \"pointer\"",
                        defaultValue: "\"auto\"",
                        name: "interactionMode"
                    },
                    {
                        description: t("swipeableRowPage.propIsFullSwipeEnabled"),
                        name: "isFullSwipeEnabled",
                        defaultValue: "false",
                        type: "boolean"
                    },
                    {
                        description: t("swipeableRowPage.propOnFullSwipeReach"),
                        name: "onFullSwipeReach",
                        type: "() => void"
                    },
                    {
                        description: t("swipeableRowPage.propOnSwipeOpen"),
                        type: "(side: \"leading\" | \"trailing\") => void",
                        name: "onSwipeOpen"
                    },
                    {
                        description: t("swipeableRowPage.propOnSwipeClose"),
                        name: "onSwipeClose",
                        type: "() => void"
                    },
                    {
                        description: t("swipeableRowPage.propIsDisabled"),
                        defaultValue: "false",
                        name: "isDisabled",
                        type: "boolean"
                    },
                    {
                        description: t("swipeableRowPage.propStyle"),
                        type: "StyleProp<ViewStyle>",
                        name: "style"
                    },
                    {
                        type: "{ themeKey, paletteKey, sharpness, gapPropagation }",
                        description: t("swipeableRowPage.propCustomTheme"),
                        name: "customTheme"
                    },
                    {
                        description: t("swipeableRowPage.propCustomLocalize"),
                        type: "{ activeLocale }",
                        name: "customLocalize"
                    }
                ]}
            />
            <Paragraph
                isMuted={true}
            >
                {t("swipeableRowPage.propsNote")}
            </Paragraph>
            <Paragraph>
                <InlineCode>
                    ISwipeRowAction
                </InlineCode>
                {" "}
                {t("swipeableRowPage.actionText")}
            </Paragraph>
            <PropsTable
                rows={[
                    {
                        description: t("swipeableRowPage.actionKey"),
                        type: "string",
                        name: "key"
                    },
                    {
                        description: t("swipeableRowPage.actionTitle"),
                        type: "string",
                        name: "title"
                    },
                    {
                        description: t("swipeableRowPage.actionIcon"),
                        type: "NCoreUIKitIcon",
                        name: "icon"
                    },
                    {
                        type: "\"neutral\" | \"info\" | \"success\" | \"warning\" | \"danger\"",
                        description: t("swipeableRowPage.actionType"),
                        defaultValue: "\"neutral\"",
                        name: "type"
                    },
                    {
                        description: t("swipeableRowPage.actionOnPress"),
                        type: "() => void",
                        name: "onPress"
                    },
                    {
                        description: t("swipeableRowPage.actionIsUndoable"),
                        name: "isUndoable",
                        type: "boolean"
                    },
                    {
                        description: t("swipeableRowPage.actionUndoMessage"),
                        name: "undoMessage",
                        type: "string"
                    },
                    {
                        description: t("swipeableRowPage.actionShortcut"),
                        name: "shortcut",
                        type: "string"
                    }
                ]}
            />
            <Paragraph>
                {t("swipeableRowPage.refText")}
            </Paragraph>
            <PropsTable
                rows={[
                    {
                        type: "(side: \"leading\" | \"trailing\") => void",
                        description: t("swipeableRowPage.refOpen"),
                        name: "open"
                    },
                    {
                        description: t("swipeableRowPage.refClose"),
                        type: "() => void",
                        name: "close"
                    }
                ]}
            />
        </Section>
        <ExampleSection
            name={NAME}
        />
    </Article>;
};
export default SwipeableRowPage;
